<template>
  <modal
    name="route-report-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '80%'"
    :scrollable="true"
  >
    <!-- Modal Header -->
    <div
      class="pb-2 pt-4 pr-4 pl-4 border-b mb-4 border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 2xl:h-24 flex items-center justify-between"
    >
      <h4 class="text-xl font-semibold">Bölge Düzenleme</h4>
      <button
        type="button"
        class="px-3 text-lg"
        @click="$modal.hide('route-report-modal')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="w-full h-96 flex items-center justify-center">
      <i class="fas fa-spinner fa-3x text-gray-400 fa-spin"></i>
    </div>

    <!-- Modal Content -->
    <div
      v-if="!isLoading && item"
      class="pb-4 px-4 pt-2 items-start justify-between"
    >
      <!-- Bölge Adı -->
      <div class="mb-4">
        <diffInput
          type="text"
          v-model="name"
          title="Bölge Adı"
          :required="true"
          class="text-base"
          maxlength="255"
        />
      </div>
      
      <!-- Polygon Tipi -->
      <div class="mb-4">
        <diffInput
          v-model="selectedPolygonType"
          :optList="polygonTypes"
          :title="$t('operations.products.modal.type')"
          type="select"
        />
      </div>
      
      <!-- Araç Sahiplik Filtreleri - İyileştirilmiş UI -->
      <div class="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
        <h3 class="text-gray-700 dark:text-gray-200 font-medium mb-3 flex items-center">
          <i class="fas fa-filter text-blue-500 mr-2"></i>
          Araç Sahiplik Filtreleme
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Bu bölgede hangi tip araçların puanlamaya dahil edileceğini seçin.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <!-- Özmal Araçlar -->
          <div 
            class="flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-200"
            :class="countOzmal 
              ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
              : 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'"
            @click="countOzmal = !countOzmal"
          >
            <div 
              class="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center"
              :class="countOzmal 
                ? 'bg-blue-500 border-blue-500 dark:bg-blue-600 dark:border-blue-600' 
                : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'"
            >
              <i v-if="countOzmal" class="fas fa-check text-white text-xs"></i>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">Özmal Araçları Sayılsın</label>
              <p class="text-xs text-gray-500 dark:text-gray-400">Şirket bünyesindeki araçlar puanlamaya dahil edilsin</p>
            </div>
          </div>
          
          <!-- Kiralık Araçlar -->
          <div 
            class="flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-200"
            :class="countRental 
              ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
              : 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'"
            @click="countRental = !countRental"
          >
            <div 
              class="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center"
              :class="countRental 
                ? 'bg-blue-500 border-blue-500 dark:bg-blue-600 dark:border-blue-600' 
                : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'"
            >
              <i v-if="countRental" class="fas fa-check text-white text-xs"></i>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">Kiralık Araçları Sayılsın</label>
              <p class="text-xs text-gray-500 dark:text-gray-400">Dış tedarikçilerden kiralanan araçlar puanlamaya dahil edilsin</p>
            </div>
          </div>
        </div>
        
        <!-- Filtre Özeti -->
        <div class="mt-3 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300">
          <i class="fas fa-info-circle text-blue-500 mr-1"></i>
          <span v-if="countOzmal && countRental">
            Tüm araçlar bölge puanlamasına dahil edilecek.
          </span>
          <span v-else-if="countOzmal">
            Sadece özmal araçlar bölge puanlamasına dahil edilecek.
          </span>
          <span v-else-if="countRental">
            Sadece kiralık araçlar bölge puanlamasına dahil edilecek.
          </span>
          <span v-else class="text-red-500">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Hiçbir araç türü seçilmedi! Lütfen en az bir araç türü seçin.
          </span>
        </div>
      </div>
      
      <!-- Map Section -->
      <div class="w-full">
        <routeReportMap
          :polygon="item.polyline"
          :lat="item.latitude"
          :lng="item.longitude"
          @handlePolygon="handlePolygon"
          @updateLocation="handleMarkerDrag"
        />
      </div>
    </div>

    <!-- Modal Footer -->
    <div
      class="border-t border-gray-200 dark:border-gray-600 px-4 py-3 bg-gray-50 dark:bg-gray-800 flex justify-end"
    >
      <button
        @click="editPolygon"
        class="px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors duration-200 flex items-center"
        :class="
          !load && isValid && (countOzmal || countRental)
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-gray-300 cursor-not-allowed text-gray-600'
        "
        :disabled="load || !isValid || (!countOzmal && !countRental)"
      >
        <span v-if="load">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          İşleniyor...
        </span>
        <span v-else>
          <i class="fas fa-save mr-2"></i>
          Kaydet
        </span>
      </button>
    </div>
  </modal>
</template>

<script>
// Local Components
import axios from "axios";
import routeReportMap from "./zoneMap.vue";
import { routeZone } from "../../../../networking/urlmanager";
import { encode } from "../../../../utils/encoder";
import diffInput from "@/components/general/diffInput.vue";

export default {
  name: "route-report-modal",
  props: ["detail"],
  components: {
    routeReportMap,
    diffInput,
  },
  data() {
    return {
      item: undefined,
      isLoading: false,
      polygon: [],
      name: "",
      load: false,
      selectedPolygonType: null,
      countOzmal: true, // Default olarak özmal araçlar sayılsın
      countRental: true, // Default olarak kiralık araçlar sayılsın
      polygonTypes:[{id:1,name:"Bekleme Noktası"},{id:2,name:"Yıkama Noktası"},{id:3,name:"Sevkiyat Noktası"}],
    };
  },
  computed: {
    isValid() {
      return (
        this.selectedPolygonType &&
        this.name &&
        this.item?.address &&
        this.polygon
      );
    },
    isMobile() {
      return window.innerWidth <= 768;
    }
  },
  methods: {
    handlePolygon(val) {
      const newPolyline = val.map((coord) => [coord.lat, coord.lng]);
      this.polygon = encode({ polyline: newPolyline });
    },
    handleMarkerDrag(location) {
      this.item.latitude = location.lat;
      this.item.longitude = location.lng;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            this.item.address = results[0].formatted_address;
          }
        }
      );
    },
    editPolygon() {
      if (!this.isValid) {
        this.swalBox(
          "warning",
          "UYARI",
          "Lütfen Tüm Alanları Doldurun.",
          false,
          this.$t("general.OkayTitle")
        );
        return;
      }
      
      if (!this.countOzmal && !this.countRental) {
        this.swalBox(
          "warning",
          "UYARI",
          "Lütfen en az bir araç türü seçin (Özmal veya Kiralık).",
          false,
          this.$t("general.OkayTitle")
        );
        return;
      }
      
      if (this.polygon === "BF") {
        this.swalBox(
          "warning",
          "UYARI",
          "Bölge çizmeden kaydedemezsiniz.",
          false,
          this.$t("general.OkayTitle")
        );
        return;
      }

      this.load = true;
      axios
        .post(
          routeZone.edit,
          {
            rowId: this.item.id,
            name: this.name,
            polyline: this.polygon,
            address: this.item.address,
            latitude: this.item.latitude,
            longitude: this.item.longitude,
            polygonType: this.selectedPolygonType,
            countOzmal: this.countOzmal ? 1 : 0,
            countRental: this.countRental ? 1 : 0
          },
          {
            headers: {
              Authorization: "Bareer " + this.$store.state.userData.token,
            },
          }
        )
        .then(() => {
          this.swalBox(
            "success",
            this.$t("general.successTitle"),
            "",
            false,
            this.$t("general.OkayTitle")
          ).then(() => {
            this.$modal.hide("route-report-modal");
          });
        })
        .catch((err) => {
          this.errorBox(err.response);
        })
        .finally(() => {
          this.load = false;
          this.$emit("refresh", true);
        });
    },
  },
  watch: {
    detail(val) {
      this.item = val;
      this.name = val.name;
      this.polygon = val?.polyline;
      this.selectedPolygonType = val.polygonType;
      this.countOzmal = val.countOzmal === 1; // 1 ise true, değilse false
      this.countRental = val.countRental === 1; // 1 ise true, değilse false
    },
  },
};
</script>
